// The one API base and the one JSON fetch, timeout included.
//
// Three modules each held a private getJson, and the copies had diverged in
// the one way that matters (#76): live.ts's carried a fifteen-second abort,
// api.ts's and panels.ts's carried none — so a backend that hung mid-restart
// (the documented Caddy-queues case, see main.ts) turned a vehicle poll loop
// into a promise nobody would ever hear from again. And because the outage
// banner requires *every* enabled feed to be failing, one hung loop
// suppressed the banner for the rest. Divergence between copies is not a
// style problem; it is how one bug ships in two of three places.
//
// NOTE for Node-tested modules: loading this file reads `import.meta.env`, a
// Vite substitution that is undefined under `node --test`. A module that a
// script test imports must not import this one — pure helpers live in
// escape.ts and friends instead.

// Same-origin by default: the Vite dev server proxies /api to the backend, and
// in production the two are served behind one host. VITE_API_BASE overrides
// for a phone build pointing at a LAN or deployed backend.
// `||` rather than `??` for the same reason as VITE_MAP_STYLE: the Docker
// build can supply these as empty strings, which `??` would not fall back on.
export const BASE = import.meta.env.VITE_API_BASE || '';

/**
 * One JSON GET against our API, which either answers or becomes an error.
 *
 * A hung backend must become an error somebody catches, not a held promise:
 * every caller already has a catch path that keeps the last good data, and
 * fifteen seconds is far beyond any healthy response from this API.
 */
export async function getJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}/api/v1${path}`, {
    headers: { Accept: 'application/json' },
    signal: AbortSignal.timeout(15_000),
  });
  if (!res.ok) throw new Error(`${path} → HTTP ${res.status}`);
  return res.json() as Promise<T>;
}
